// src/components/MigrateLtiLinks.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

/**
 * MigrateLtiLinks
 * -----------------------------------------------------------
 * Scans and migrates embedded LTI links across:
 *  • Assignment/Test instructions (contentHandler.instructions) → PATCH v1 assessments
 *  • Question sections (questionText, correct/incorrect feedback) → PATCH v1 questions
 *  • Question answers[i].answerText → PATCH v1 questions (answers array)
 *  • Ultra Document body (resource/x-bb-document.body) → PATCH public v1 contents
 *  • Content-level LTI links (resource/x-bb-blti-link) → PATCH public v1 contents
 *
 * Course selection:
 *  • If ?courseUuid=... present → public v1 contents use /courses/uuid:{uuid}
 *  • Else (PK only)             → public v1 contents use /courses/{courseId} (no "courseId:" prefix)
 *  • v1 questions/assessments APIs always require PK (courseId)
 *
 * Robust URL parsing, includes questionType on patches, no preview UI, title removed.
 * Document rows show the PARENT item’s title. CSV export & import. Bulk "Patch All Modified".
 */

const MigrateLtiLinks = ({ platformUrl }) => {
    const qs = new URLSearchParams(window.location.search);
    thetoken: {
    }
    const token = qs.get("token");
    const courseUuid = qs.get("courseUuid") || "";

    // PK: prefer ?courseId=… else read from localStorage ("courseId" key)
    const courseId = qs.get("courseId") || localStorage.getItem("courseId") || "";

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [rows, setRows] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [bulkStatus, setBulkStatus] = useState("");

    const fileInputRef = useRef(null);

    const authHeaders = useMemo(
        () => ({ headers: { Authorization: `Bearer ${token}` } }),
        [token]
    );

    /** ----------------- Helpers ----------------- */

    const contentsCoursePath = useMemo(() => {
        if (courseUuid) return `uuid:${courseUuid}`;
        if (courseId) return `${courseId}`; // raw PK, no prefix
        return "";
    }, [courseUuid, courseId]);

    const getPaged = async (url) => {
        let results = [];
        let next = url;
        while (next) {
            const { data } = await axios.get(next, authHeaders);
            results = results.concat(data?.results || []);
            next = data?.paging?.nextPage || null;
        }
        return results;
    };

    const isContainerHandler = (handlerId = "") => {
        const id = String(handlerId || "").toLowerCase();
        return (
            id.includes("folder") ||
            id.includes("learningmodule") ||
            id.includes("lesson") ||
            id.includes("module")
        );
    };

    const isAssessmentLink = (item) =>
        String(item?.contentHandler?.id || "")
            .toLowerCase()
            .includes("asmt-test-link");

    const isDocument = (item) =>
        String(item?.contentHandler?.id || "").toLowerCase() === "resource/x-bb-document";

    const isBltiLink = (item) =>
        String(item?.contentHandler?.id || "").toLowerCase() === "resource/x-bb-blti-link";

    const fetchRoot = async () =>
        getPaged(
            `${platformUrl}/learn/api/public/v1/courses/${contentsCoursePath}/contents?parentId=ROOT&limit=200`
        );

    const fetchChildren = async (contentId) =>
        getPaged(
            `${platformUrl}/learn/api/public/v1/courses/${contentsCoursePath}/contents/${contentId}/children?limit=200`
        );

    const crawlContents = async () => {
        const all = [];
        const q = [{ id: "ROOT", _root: true }];

        const rootKids = await fetchRoot();
        for (const ch of rootKids) {
            all.push({ ...ch, parentId: "ROOT" });
            q.push({
                id: ch.id,
                handlerId: ch?.contentHandler?.id || "",
                hasChildren: !!ch?.hasChildren,
            });
        }

        while (q.length) {
            const node = q.shift();
            if (!node || node._root) continue;

            const cur = all.find((i) => i.id === node.id);
            const handlerId = node.handlerId || cur?.contentHandler?.id || "";
            const nodeHasChildren = node.hasChildren ?? cur?.hasChildren ?? false;

            if (!(nodeHasChildren || isContainerHandler(handlerId))) continue;

            try {
                const kids = await fetchChildren(node.id);
                for (const k of kids) {
                    all.push({ ...k, parentId: node.id });
                    q.push({
                        id: k.id,
                        handlerId: k?.contentHandler?.id || "",
                        hasChildren: !!k?.hasChildren,
                    });
                }
            } catch (e) {
                console.warn("Children fetch failed for", node.id, e?.message);
            }
        }

        return all;
    };

    // v1 (PK-based) assessment APIs
    const fetchAssessmentQuestions = async (courseIdPK, assessmentId) => {
        if (!courseIdPK) return [];
        const url = `${platformUrl}/learn/api/v1/courses/${encodeURIComponent(
            courseIdPK
        )}/assessments/${encodeURIComponent(
            assessmentId
        )}/questions?expand=alignedGoals,usageCount&limit=10000`;
        const data = await axios.get(url, authHeaders).then((r) => r.data);
        return data?.results || [];
    };

    /** ----------------- HTML/URL parsing ----------------- */

    const decodeEntities = (s = "") =>
        s
            .replace(/&quot;/g, '"')
            .replace(/&#34;/g, '"')
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">");

    const encodeEntities = (s = "") =>
        s
            .replace(/&/g, "&amp;")
            .replace(/"/g, "&quot;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");

    const isLikelyHttpUrl = (s) => {
        if (!s) return false;
        try {
            if (!/^[a-z]+:\/\//i.test(String(s))) return false;
            const u = new URL(String(s));
            return /^https?:$/.test(u.protocol);
        } catch {
            return false;
        }
    };

    const isBadHref = (s) => {
        if (!s) return true;
        const v = String(s).trim().toLowerCase();
        if (v === "undefined" || v === "null" || v === "#" || v === "about:blank") return true;
        if (v.startsWith("undefined")) return true; // "undefined?Kq3..."
        if (v.startsWith("javascript:")) return true;
        if (!isLikelyHttpUrl(s)) return true;
        return false;
    };

    // FIRST <a data-bbtype="embedded-app">
    const findEmbeddedAnchor = (html = "") => {
        if (!html) return null;
        const anchorRe = /<a\b[^>]*\bdata-bbtype=["']embedded-app["'][^>]*>([\s\S]*?)<\/a>/i;
        const m = html.match(anchorRe);
        if (!m) return null;

        const full = m[0];
        const hrefMatch = full.match(/\bhref=["']([^"']*)["']/i);
        const rawHref = hrefMatch ? hrefMatch[1] : "";

        const bbMatch = full.match(/\bdata-bbfile=["']([^"']*)["']/i);
        let bbJson = {};
        if (bbMatch && bbMatch[1]) {
            try {
                bbJson = JSON.parse(decodeEntities(bbMatch[1]));
            } catch {
                bbJson = {};
            }
        }

        const bbUrl = bbJson?.url || "";
        const chosenHref = bbUrl || (!isBadHref(rawHref) ? rawHref : "");
        const start = m.index ?? html.indexOf(full);
        const end = start + full.length;

        return {
            match: full,
            start,
            end,
            href: chosenHref,
            rawHref,
            json: bbJson,
            inner: m[1] || "",
        };
    };

    // ALL <a data-bbtype="embedded-app">
    const findAllEmbeddedAnchors = (html = "") => {
        if (!html) return [];
        const re = /<a\b[^>]*\bdata-bbtype=["']embedded-app["'][^>]*>([\s\S]*?)<\/a>/gi;
        const out = [];
        let m;
        while ((m = re.exec(html)) !== null) {
            const full = m[0];
            const hrefMatch = full.match(/\bhref=["']([^"']*)["']/i);
            const rawHref = hrefMatch ? hrefMatch[1] : "";

            const bbMatch = full.match(/\bdata-bbfile=["']([^"']*)["']/i);
            let bbJson = {};
            if (bbMatch && bbMatch[1]) {
                try {
                    bbJson = JSON.parse(decodeEntities(bbMatch[1]));
                } catch {
                    bbJson = {};
                }
            }

            const bbUrl = bbJson?.url || "";
            const chosenHref = bbUrl || (!isBadHref(rawHref) ? rawHref : "");
            const start = m.index;
            const end = start + full.length;

            out.push({
                match: full,
                start,
                end,
                href: chosenHref,
                rawHref,
                json: bbJson,
                inner: m[1] || "",
            });
        }
        return out;
    };

    const rewriteEmbeddedAnchor = (html = "", newUrl, newCustomParams) => {
        const found = findEmbeddedAnchor(html);
        if (!found) return html;

        const { match: oldAnchor, json: oldJson } = found;

        const bbfile = {
            ...oldJson,
            url: newUrl ?? oldJson?.url ?? "",
            customParameters:
                typeof newCustomParams === "object" && newCustomParams !== null
                    ? newCustomParams
                    : oldJson?.customParameters || undefined,
        };

        const bbfileAttr = encodeEntities(JSON.stringify(bbfile));
        const finalHref = bbfile.url || "";

        let newAnchor = oldAnchor;
        if (/\bhref=/.test(newAnchor)) {
            newAnchor = newAnchor.replace(/\bhref=["'][^"']*["']/i, `href="${finalHref}"`);
        } else {
            newAnchor = newAnchor.replace(/^<a\b/i, `<a href="${finalHref}"`);
        }

        if (/\bdata-bbfile=/.test(newAnchor)) {
            newAnchor = newAnchor.replace(/\bdata-bbfile=["'][^"']*["']/i, `data-bbfile="${bbfileAttr}"`);
        } else {
            newAnchor = newAnchor.replace(/^<a\b/i, `<a data-bbfile="${bbfileAttr}"`);
        }

        newAnchor = newAnchor.replace(/(<a\b[^>]*>)([\s\S]*?)(<\/a>)/i, (_, open, content, close) => {
            const s = String(content || "").trim();
            const looksLikeUrl = /^https?:\/\//i.test(s);
            const isUndef = s.toLowerCase() === "undefined" || s.toLowerCase().startsWith("undefined?");
            const innerText = looksLikeUrl || isUndef ? finalHref : content;
            return `${open}${innerText}${close}`;
        });

        return html.replace(oldAnchor, newAnchor);
    };

    const rewriteEmbeddedAnchorAt = (html = "", index, newUrl, newCustomParams) => {
        const all = findAllEmbeddedAnchors(html);
        if (!all.length || index < 0 || index >= all.length) return html;

        const target = all[index];
        const oldAnchor = target.match;
        const oldJson = target.json || {};

        const bbfile = {
            ...oldJson,
            url: newUrl ?? oldJson?.url ?? "",
            customParameters:
                typeof newCustomParams === "object" && newCustomParams !== null
                    ? newCustomParams
                    : oldJson?.customParameters || undefined,
        };

        const bbfileAttr = encodeEntities(JSON.stringify(bbfile));
        const finalHref = bbfile.url || "";

        let newAnchor = oldAnchor;
        if (/\bhref=/.test(newAnchor)) {
            newAnchor = newAnchor.replace(/\bhref=["'][^"']*["']/i, `href="${finalHref}"`);
        } else {
            newAnchor = newAnchor.replace(/^<a\b/i, `<a href="${finalHref}"`);
        }

        if (/\bdata-bbfile=/.test(newAnchor)) {
            newAnchor = newAnchor.replace(/\bdata-bbfile=["'][^"']*["']/i, `data-bbfile="${bbfileAttr}"`);
        } else {
            newAnchor = newAnchor.replace(/^<a\b/i, `<a data-bbfile="${bbfileAttr}"`);
        }

        newAnchor = newAnchor.replace(/(<a\b[^>]*>)([\s\S]*?)(<\/a>)/i, (_, open, content, close) => {
            const s = String(content || "").trim();
            const looksLikeUrl = /^https?:\/\//i.test(s);
            const isUndef = s.toLowerCase() === "undefined" || s.toLowerCase().startsWith("undefined?");
            const innerText = looksLikeUrl || isUndef ? finalHref : content;
            return `${open}${innerText}${close}`;
        });

        return html.slice(0, target.start) + newAnchor + html.slice(target.end);
    };

    /** ----------------- Row builders ----------------- */

    const buildRowsForQuestion = ({ contentItemTitle, assessmentId, question, columnId }) => {
        const out = [];
        const pushRowIfAnchor = (section, raw, extra = {}) => {
            const found = findEmbeddedAnchor(raw || "");
            const url = found?.href || "";
            if (!found || !url) return;

            const currentParams = found.json?.customParameters;

            const questionType =
                question?.questionType ||
                question?.type ||
                (question?.questionHandler && question.questionHandler.type) ||
                "";

            out.push({
                key: `${assessmentId}:${question.id}:${section}${
                    extra.answerIndex != null ? ":" + extra.answerIndex : ""
                }`,
                title: contentItemTitle || "(Untitled assessment)",
                assessmentId,
                questionId: question.id,
                questionType,
                section,
                columnId,
                currentUrl: url,
                currentParams,
                newUrl: url,
                newParamsText:
                    currentParams != null ? JSON.stringify(currentParams, null, 2) : "{}",
                originalHtml: raw || "",
                status: "",
                answerIndex: extra.answerIndex,
                answersSnapshot: extra.answersSnapshot,
                contentId: "", // not applicable for questions
            });
        };

        pushRowIfAnchor("questionText", question?.questionText?.rawText);
        pushRowIfAnchor("correctResponseFeedback", question?.correctResponseFeedback?.rawText);
        pushRowIfAnchor("incorrectResponseFeedback", question?.incorrectResponseFeedback?.rawText);

        if (Array.isArray(question?.answers)) {
            const answersSnapshot = JSON.parse(JSON.stringify(question.answers));
            question.answers.forEach((ans, idx) => {
                const raw = ans?.answerText?.rawText;
                pushRowIfAnchor("answerText", raw, { answerIndex: idx, answersSnapshot });
            });
        }

        return out;
    };

    const buildRowForInstructions = (contentItem) => {
        const html = contentItem?.contentHandler?.instructions;
        const found = findEmbeddedAnchor(html || "");
        const url = found?.href || "";
        if (!found || !url) return null;

        const currentParams = found.json?.customParameters;

        return {
            key: `instr:${contentItem.id}`,
            title: contentItem?.title || "(Untitled assessment)",
            assessmentId: contentItem?.contentHandler?.assessmentId || "",
            questionId: "",
            questionType: "",
            section: "instructions",
            columnId: contentItem?.contentHandler?.gradeColumnId || "",
            contentId: contentItem.id,
            currentUrl: url,
            currentParams,
            newUrl: url,
            newParamsText:
                currentParams != null ? JSON.stringify(currentParams, null, 2) : "{}",
            originalHtml: html || "",
            status: "",
            answerIndex: undefined,
        };
    };

    const buildRowsForDocumentBody = (contentItem, displayTitle) => {
        const html = contentItem?.body || "";
        if (!html) return [];
        const anchors = findAllEmbeddedAnchors(html);
        if (!anchors.length) return [];

        return anchors.map((a, idx) => ({
            key: `doc:${contentItem.id}:${idx}`,
            title: displayTitle || contentItem?.title || "(Untitled document)",
            assessmentId: "",
            questionId: "",
            questionType: "",
            section: "documentBody",
            anchorIndex: idx,
            columnId: "",
            contentId: contentItem.id,
            currentUrl: a.href || "",
            currentParams: a.json?.customParameters,
            newUrl: a.href || "",
            newParamsText:
                a.json?.customParameters != null
                    ? JSON.stringify(a.json.customParameters, null, 2)
                    : "{}",
            originalHtml: html,
            status: "",
            answerIndex: undefined,
        }));
    };

    const buildRowForBltiLink = (contentItem, displayTitle) => {
        const ch = contentItem?.contentHandler || {};
        const url = ch?.url || "";
        const params = ch?.customParameters || undefined;

        if (!isLikelyHttpUrl(url)) return null;

        return {
            key: `blti:${contentItem.id}`,
            title: displayTitle || contentItem?.title || "(LTI Link)",
            assessmentId: "",
            questionId: "",
            questionType: "",
            section: "bltiLink",
            anchorIndex: null,
            columnId: "",
            contentId: contentItem.id,
            currentUrl: url,
            currentParams: params,
            newUrl: url,
            newParamsText: params != null ? JSON.stringify(params, null, 2) : "{}",
            originalHandler: ch,
            originalHtml: "",
            status: "",
            answerIndex: undefined,
        };
    };

    /** ----------------- Load flow ----------------- */
    useEffect(() => {
        (async () => {
            if (!token) {
                setError("Missing access token (?token=...).");
                setLoading(false);
                return;
            }
            if (!contentsCoursePath) {
                setError(
                    "Missing course identifier. Provide ?courseUuid=... OR ensure courseId (PK) exists (query or localStorage)."
                );
                setLoading(false);
                return;
            }

            try {
                setLoading(true);

                const contentList = await crawlContents();

                const byId = contentList.reduce((acc, it) => {
                    acc[it.id] = it;
                    return acc;
                }, {});
                const getParentTitle = (it) =>
                    (it?.parentId && byId[it.parentId]?.title) ||
                    it?.title ||
                    "(Untitled document)";

                const assessItems = contentList.filter(isAssessmentLink);
                const documentItems = contentList.filter(isDocument);
                theblti: {
                }
                const bltiItems = contentList.filter(isBltiLink);

                const assessmentInfos = {};
                const rowsInit = [];

                for (const it of assessItems) {
                    const aid = it?.contentHandler?.assessmentId;
                    if (aid) {
                        assessmentInfos[aid] ||= {
                            title: it?.title || "(Untitled)",
                            columnId: it?.contentHandler?.gradeColumnId || "",
                        };
                    }
                    const instrRow = buildRowForInstructions(it);
                    if (instrRow) rowsInit.push(instrRow);
                }

                for (const doc of documentItems) {
                    const displayTitle = getParentTitle(doc);
                    const docRows = buildRowsForDocumentBody(doc, displayTitle);
                    rowsInit.push(...docRows);
                }

                for (const it of bltiItems) {
                    const row = buildRowForBltiLink(it, it?.title);
                    if (row) rowsInit.push(row);
                }

                for (const [assessmentId, info] of Object.entries(assessmentInfos)) {
                    try {
                        if (!courseId) {
                            console.warn("Skipping questions load — no courseId (PK) available.");
                            continue;
                        }
                        const questions = await fetchAssessmentQuestions(courseId, assessmentId);
                        for (const q of questions) {
                            const rset = buildRowsForQuestion({
                                contentItemTitle: info.title,
                                assessmentId,
                                question: q,
                                columnId: info.columnId,
                            });
                            rowsInit.push(...rset);
                        }
                    } catch (e) {
                        console.warn("Failed to fetch questions for", assessmentId, e?.message);
                    }
                }

                setRows(rowsInit);
            } catch (e) {
                setError(e?.response?.data?.message || e?.message || "Failed to load data.");
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [platformUrl, token, contentsCoursePath, courseId, refreshKey]);

    /** ----------------- PATCH helpers ----------------- */

    const patchQuestionSection = async ({
                                            courseIdPK,
                                            assessmentId,
                                            questionId,
                                            questionType,
                                            columnId,
                                            section,
                                            rawHtml,
                                        }) => {
        const url = `${platformUrl}/learn/api/v1/courses/${encodeURIComponent(
            courseIdPK
        )}/assessments/${encodeURIComponent(
            assessmentId
        )}/questions/${encodeURIComponent(
            questionId
        )}?columnId=${encodeURIComponent(columnId || "")}&expand=alignedGoals%2CusageCount`;

        const payload = {
            id: questionId,
            questionType,
            [section]: {
                rawText: rawHtml,
                displayText: rawHtml,
            },
        };

        const { data } = await axios.patch(url, payload, authHeaders);
        return data;
    };

    const patchQuestionAnswers = async ({
                                            courseIdPK,
                                            assessmentId,
                                            questionId,
                                            questionType,
                                            columnId,
                                            updatedAnswersArray,
                                        }) => {
        const url = `${platformUrl}/learn/api/v1/courses/${encodeURIComponent(
            courseIdPK
        )}/assessments/${encodeURIComponent(
            assessmentId
        )}/questions/${encodeURIComponent(
            questionId
        )}?columnId=${encodeURIComponent(columnId || "")}&expand=alignedGoals%2CusageCount`;

        const payload = {
            id: questionId,
            questionType,
            answers: updatedAnswersArray,
        };

        const { data } = await axios.patch(url, payload, authHeaders);
        return data;
    };

    const patchAssessmentInstructions = async ({ courseIdPK, assessmentId, rawHtml }) => {
        const url = `${platformUrl}/learn/api/v1/courses/${encodeURIComponent(
            courseIdPK
        )}/assessments/${encodeURIComponent(assessmentId)}`;
        const payload = {
            id: assessmentId,
            instructions: {
                rawText: rawHtml,
            },
        };
        const { data } = await axios.patch(url, payload, authHeaders);
        return data;
    };

    const patchDocumentBody = async ({ courseSpecifierPath, contentId, rawHtml }) => {
        const url = `${platformUrl}/learn/api/public/v1/courses/${courseSpecifierPath}/contents/${encodeURIComponent(
            contentId
        )}`;
        const payload = {
            id: contentId,
            body: rawHtml,
        };
        const { data } = await axios.patch(url, payload, authHeaders);
        return data;
    };

    const patchContentBltiLink = async ({ courseSpecifierPath, contentId, originalHandler, newUrl, newParams }) => {
        const url = `${platformUrl}/learn/api/public/v1/courses/${courseSpecifierPath}/contents/${encodeURIComponent(
            contentId
        )}`;
        const contentHandler = {
            ...(originalHandler || {}),
            id: "resource/x-bb-blti-link",
            url: newUrl,
            customParameters: newParams,
        };
        const payload = { id: contentId, contentHandler };
        const { data } = await axios.patch(url, payload, authHeaders);
        return data;
    };

    /** ----------------- CSV Export/Import ----------------- */

    const escapeCsv = (val) => {
        if (val == null) return "";
        const s = String(val);
        if (/[",\n\r]|^\s|\s$/.test(s)) {
            return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
    };

    const rowsToCsv = (rws) => {
        const na = (v) => (v === "—" || v == null ? "" : v);

        const headers = [
            "title",
            "section",
            "idx",
            "assessmentId",
            "questionId",
            "questionType",
            "contentId",
            "columnId",
            "currentUrl",
            "currentCustomParameters",
            "newUrl",
            "newCustomParameters",
            "status",
        ];
        const lines = [headers.map(escapeCsv).join(",")];

        rws.forEach((r) => {
            const idx =
                r.section === "answerText"
                    ? r.answerIndex
                    : r.section === "documentBody"
                        ? r.anchorIndex
                        : "";

            const curParams = r.currentParams != null ? JSON.stringify(r.currentParams) : "";
            const newParams = r.newParamsText != null ? r.newParamsText : "";

            const rowArr = [
                r.title || "",
                r.section || "",
                idx,
                r.assessmentId || "",
                na(r.questionId) || "",
                na(r.questionType) || "",
                r.contentId || "",
                r.columnId || "",
                r.currentUrl || "",
                curParams,
                r.newUrl || "",
                newParams,
                r.status || "",
            ];
            lines.push(rowArr.map(escapeCsv).join(","));
        });

        return lines.join("\r\n");
    };

    const handleExportCsv = () => {
        try {
            const csv = rowsToCsv(rows);
            // Prepend BOM so Excel reliably detects UTF-8
            const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "migrate-lti-links.csv";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            alert(`CSV export failed: ${e?.message || e}`);
        }
    };

    // Minimal CSV parser that handles quotes, commas, newlines (ESLint-safe)
    const parseCsv = (text) => {
        // Strip BOM if present
        if (text && text.charCodeAt(0) === 0xfeff) text = text.slice(1);
        const rowsOut = [];
        let i = 0, field = "", inQuotes = false, row = [];

        const pushField = () => {
            const isInitialEmptyField = rowsOut.length === 0 && field === "" && row.length === 0;
            if (!isInitialEmptyField) {
                row.push(field);
            }
            field = "";
        };
        const pushRow = () => {
            if (row.length > 0 && !(row.length === 1 && row[0] === "")) rowsOut.push(row);
            row = [];
        };

        while (i < text.length) {
            const ch = text[i];

            if (inQuotes) {
                if (ch === '"') {
                    if (text[i + 1] === '"') {
                        field += '"';
                        i += 2;
                    } else {
                        inQuotes = false;
                        i++;
                    }
                } else {
                    field += ch;
                    i++;
                }
            } else {
                if (ch === '"') {
                    inQuotes = true; i++;
                } else if (ch === ",") {
                    pushField(); i++;
                } else if (ch === "\r") {
                    pushField();
                    if (text[i + 1] === "\n") i++;
                    i++;
                    pushRow();
                } else if (ch === "\n") {
                    pushField(); i++; pushRow();
                } else {
                    field += ch; i++;
                }
            }
        }
        pushField(); pushRow();

        if (rowsOut.length === 0) return [];
        const headers = rowsOut[0].map((h) => h.trim());
        return rowsOut.slice(1).map((r) => {
            const o = {};
            headers.forEach((h, idx) => (o[h] = r[idx] ?? ""));
            return o;
        });
    };

    // Apply imported CSV rows onto current state
    const applyImportedRows = (items) => {
        const safeTrim = (v) => (v == null ? "" : String(v).trim());
        const parseIntMaybe = (v) => {
            const s = safeTrim(v);
            if (s === "") return undefined;
            const n = Number(s);
            return Number.isFinite(n) ? n : undefined;
        };

        setRows((prev) => {
            const next = prev.map((r) => ({ ...r }));

            const bySignature = (obj) => {
                const section = safeTrim(obj.section);
                const assessmentId = safeTrim(obj.assessmentId);
                const questionId = safeTrim(obj.questionId);
                const contentId = safeTrim(obj.contentId);
                const idx = parseIntMaybe(obj.idx);
                return { section, assessmentId, questionId, contentId, idx };
            };

            const matches = (r, sig) => {
                if ((r.section || "") !== sig.section) return false;
                if ((r.assessmentId || "") !== sig.assessmentId) return false;
                if ((r.questionId || "") !== sig.questionId) return false;
                if ((r.contentId || "") !== sig.contentId) return false;
                if (r.section === "answerText") return r.answerIndex === sig.idx;
                if (r.section === "documentBody") return r.anchorIndex === sig.idx;
                return true;
            };

            items.forEach((item) => {
                const sig = bySignature(item);
                const newUrl = safeTrim(item.newUrl);
                const newParamsText = String(item.newCustomParameters ?? "").trim() || "{}";

                const target = next.find((r) => matches(r, sig));
                if (target) {
                    if (newUrl) target.newUrl = newUrl;
                    if (newParamsText) target.newParamsText = newParamsText;
                    target.status =
                        target.status && target.status.startsWith("Patched")
                            ? target.status
                            : "Loaded from CSV";
                }
            });

            return next;
        });
    };

    const handleImportCsv = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = String(e.target.result || "");
                const items = parseCsv(text);
                if (!items.length) {
                    alert("CSV appears empty or malformed.");
                    return;
                }
                applyImportedRows(items);
            } catch (err) {
                alert(`CSV import failed: ${err?.message || err}`);
            } finally {
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
        };
        reader.readAsText(file);
    };

    /** ----------------- Bulk Patch ----------------- */

    const stableStringify = (obj) => {
        if (obj == null) return "";
        try {
            return JSON.stringify(JSON.parse(obj), Object.keys(JSON.parse(obj)).sort());
        } catch {
            try {
                return JSON.stringify(obj, Object.keys(obj).sort());
            } catch {
                return String(obj);
            }
        }
    };

    const isRowModified = (r) => {
        const urlDiff = (r.newUrl || "") !== (r.currentUrl || "");
        let newObj = {};
        try {
            newObj = JSON.parse(r.newParamsText || "{}");
        } catch {
            /* ignore */
        }
        const curStr = r.currentParams != null ? stableStringify(r.currentParams) : "";
        const newStr = stableStringify(newObj);
        const paramsDiff = curStr !== newStr;
        return urlDiff || paramsDiff;
    };

    const patchAllModified = async () => {
        const candidates = rows.filter(isRowModified);
        if (candidates.length === 0) {
            setBulkStatus("No modified rows detected.");
            return;
        }

        setBulkStatus(`Patching ${candidates.length} row(s)…`);
        for (const r of candidates) {
            // eslint-disable-next-line no-await-in-loop
            await (async () => {
                try {
                    await handlePatch(r);
                } catch {
                    // per-row status already handled
                }
            })();
        }
        setBulkStatus(`Done. Patched ${candidates.length} row(s).`);
    };

    /** ----------------- UI actions ----------------- */

    const setRowField = (key, patch) =>
        setRows((arr) => arr.map((r) => (r.key === key ? { ...r, ...patch } : r)));

    const handlePatch = async (row) => {
        let newParamsObj = {};
        try {
            newParamsObj = JSON.parse(row.newParamsText || "{}");
        } catch {
            setRowField(row.key, { status: "Error: customParameters is not valid JSON" });
            return;
        }

        if (!courseId && row.section !== "documentBody" && row.section !== "bltiLink") {
            setRowField(row.key, { status: "Error: courseId (PK) not available for PATCH" });
            return;
        }

        try {
            setRowField(row.key, { status: "Patching…" });

            if (row.section === "instructions") {
                const newHtml = rewriteEmbeddedAnchor(row.originalHtml, row.newUrl, newParamsObj);
                await patchAssessmentInstructions({
                    courseIdPK: courseId,
                    assessmentId: row.assessmentId,
                    rawHtml: newHtml,
                });
                setRowField(row.key, {
                    status: "Patched",
                    originalHtml: newHtml,
                    currentUrl: row.newUrl,
                    currentParams: newParamsObj,
                });
            } else if (row.section === "answerText") {
                const answers = Array.isArray(row.answersSnapshot)
                    ? JSON.parse(JSON.stringify(row.answersSnapshot))
                    : [];
                const idx = row.answerIndex;
                if (idx == null || !answers[idx]) {
                    setRowField(row.key, { status: "Error: answer index missing" });
                    return;
                }
                const original = answers[idx];
                const baseHtml = original?.answerText?.rawText || row.originalHtml;
                const newHtml = rewriteEmbeddedAnchor(baseHtml, row.newUrl, newParamsObj);

                answers[idx] = {
                    ...original,
                    answerText: {
                        ...(original?.answerText || {}),
                        rawText: newHtml,
                        displayText: newHtml,
                    },
                };

                await patchQuestionAnswers({
                    courseIdPK: courseId,
                    assessmentId: row.assessmentId,
                    questionId: row.questionId,
                    questionType: row.questionType,
                    columnId: row.columnId || "",
                    updatedAnswersArray: answers,
                });

                setRowField(row.key, {
                    status: "Patched",
                    originalHtml: newHtml,
                    currentUrl: row.newUrl,
                    currentParams: newParamsObj,
                    answersSnapshot: answers,
                });
            } else if (row.section === "documentBody") {
                const nextHtml = rewriteEmbeddedAnchorAt(
                    row.originalHtml,
                    row.anchorIndex,
                    row.newUrl,
                    newParamsObj
                );

                await patchDocumentBody({
                    courseSpecifierPath: contentsCoursePath,
                    contentId: row.contentId,
                    rawHtml: nextHtml,
                });

                setRowField(row.key, {
                    status: "Patched",
                    originalHtml: nextHtml,
                    currentUrl: row.newUrl,
                    currentParams: newParamsObj,
                });

                setRows((arr) =>
                    arr.map((r) =>
                        r.section === "documentBody" && r.contentId === row.contentId
                            ? { ...r, originalHtml: nextHtml }
                            : r
                    )
                );
            } else if (row.section === "bltiLink") {
                await patchContentBltiLink({
                    courseSpecifierPath: contentsCoursePath,
                    contentId: row.contentId,
                    originalHandler: row.originalHandler,
                    newUrl: row.newUrl,
                    newParams: newParamsObj,
                });

                setRowField(row.key, {
                    status: "Patched",
                    currentUrl: row.newUrl,
                    currentParams: newParamsObj,
                });
            } else {
                const newHtml = rewriteEmbeddedAnchor(row.originalHtml, row.newUrl, newParamsObj);
                await patchQuestionSection({
                    courseIdPK: courseId,
                    assessmentId: row.assessmentId,
                    questionId: row.questionId,
                    questionType: row.questionType,
                    columnId: row.columnId || "",
                    section: row.section,
                    rawHtml: newHtml,
                });
                setRowField(row.key, {
                    status: "Patched",
                    originalHtml: newHtml,
                    currentUrl: row.newUrl,
                    currentParams: newParamsObj,
                });
            }
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || "Patch failed.";
            setRowField(row.key, { status: `Error: ${msg}` });
        }
    };

    /** ----------------- Render ----------------- */

    if (loading) return <div className="bb-container">Loading…</div>;
    if (error)
        return (
            <div className="bb-container bb-error">
                <p>{error}</p>
            </div>
        );

    return (
        <div className="bb-container">
            <div className="bb-header" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {/* Title intentionally removed */}
                <button className="bb-btn" onClick={() => setRefreshKey((k) => k + 1)}>
                    Refresh / Re-scan
                </button>
                <button className="bb-btn" onClick={handleExportCsv}>
                    Export CSV
                </button>
                <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv,text/csv"
                    style={{ display: "none" }}
                    onChange={(e) => handleImportCsv(e.target.files?.[0])}
                />
                <button className="bb-btn" onClick={() => fileInputRef.current?.click()}>
                    Import CSV
                </button>
                <button className="bb-btn" onClick={patchAllModified}>
                    Patch All Modified
                </button>
                {bulkStatus && (
                    <span style={{ marginLeft: 8, fontSize: "0.9em", opacity: 0.8 }}>{bulkStatus}</span>
                )}
            </div>

            <div style={{ margin: "8px 0", opacity: 0.8 }}>
                Found {rows.length} embedded/mappable LTI location{rows.length === 1 ? "" : "s"} across instructions, questions, feedback, answers, documents, and LTI content items.
            </div>

            <table className="bb-table">
                <thead>
                <tr>
                    <th style={{ width: "18%" }}>Item Title</th>
                    <th style={{ width: "10%" }}>Assessment ID</th>
                    <th style={{ width: "10%" }}>Question ID</th>
                    <th style={{ width: "10%" }}>Question Type</th>
                    <th style={{ width: "12%" }}>Section</th>
                    <th style={{ width: "6%" }}>Idx</th>
                    <th style={{ width: "17%" }}>Current URL</th>
                    <th style={{ width: "17%" }}>Custom Parameters</th>
                </tr>
                </thead>
                <tbody>
                {rows.length === 0 ? (
                    <tr>
                        <td colSpan={8}>No LTI links found.</td>
                    </tr>
                ) : (
                    rows.map((r) => {
                        const idx =
                            r.section === "answerText"
                                ? r.answerIndex
                                : r.section === "documentBody"
                                    ? r.anchorIndex
                                    : "—";
                        return (
                            <tr key={r.key}>
                                <td>
                                    <div className="bb-strong">{r.title}</div>
                                    <div style={{ fontSize: "0.85em", opacity: 0.75 }}>
                                        {(r.section === "instructions" ||
                                            r.section === "documentBody" ||
                                            r.section === "bltiLink") && <>Content ID: {r.contentId} · </>}
                                        {r.columnId ? <>Column ID: {r.columnId}</> : null}
                                    </div>
                                </td>
                                <td>{r.assessmentId || "—"}</td>
                                <td>{r.questionId ? r.questionId : "—"}</td>
                                <td>{r.questionType ? r.questionType : "—"}</td>
                                <td>{r.section}</td>
                                <td>{idx}</td>
                                <td style={{ minWidth: 260 }}>
                                    <div style={{ marginBottom: 4, fontSize: "0.85em", opacity: 0.7 }}>Current</div>
                                    <div style={{ wordBreak: "break-all" }}>{r.currentUrl || "—"}</div>
                                    <div style={{ marginTop: 8, fontSize: "0.85em", opacity: 0.7 }}>New</div>
                                    <textarea
                                        className="bb-input"
                                        value={r.newUrl}
                                        onChange={(e) => setRowField(r.key, { newUrl: e.target.value })}
                                        rows={3}
                                        wrap="off"
                                        style={{
                                            width: "100%",
                                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                            whiteSpace: "pre",
                                        }}
                                        placeholder="https://your-new-lti-launch..."
                                    />
                                </td>
                                <td>
                                    <div style={{ marginBottom: 4, fontSize: "0.85em", opacity: 0.7 }}>Current</div>
                                    <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                      {r.currentParams ? JSON.stringify(r.currentParams, null, 2) : "—"}
                    </pre>
                                    <div style={{ margin: "8px 0 4px", fontSize: "0.85em", opacity: 0.7 }}>
                                        New
                                    </div>
                                    <textarea
                                        className="bb-textarea"
                                        value={r.newParamsText}
                                        onChange={(e) => setRowField(r.key, { newParamsText: e.target.value })}
                                        rows={6}
                                        spellCheck={false}
                                        style={{ width: "100%" }}
                                    />
                                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                                        <button className="bb-btn" onClick={() => handlePatch(r)}>
                                            Patch {r.section}
                                        </button>
                                        {isRowModified(r) && (
                                            <span style={{ fontSize: "0.85em", color: "#0a7" }}>Modified</span>
                                        )}
                                    </div>
                                    <div style={{ marginTop: 6, fontSize: "0.9em" }}>
                                        {r.status && (
                                            <span className={r.status.startsWith("Error") ? "bb-error" : "bb-success"}>
                          {r.status}
                        </span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })
                )}
                </tbody>
            </table>
        </div>
    );
};

export default MigrateLtiLinks;
